import { useState } from "react";
import { Printer, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Invoice, whatsappShareText, waLink } from "./Invoice";

export const ORDER_STATUSES = ["new", "accepted", "preparing", "ready", "completed", "cancelled"];

export function OrderDetail({ order, onClose, onUpdated }) {
  const { restaurant } = useAuth();
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(order?.payment_method || "cash");
  const [ref, setRef] = useState(order?.payment_reference || "");
  if (!order) return null;

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      onUpdated(data);
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const setStatus = (s) => run(() => api.patch(`/orders/${order.id}/status`, { status: s }), `Marked ${s}`);
  const setPaid = (paid) =>
    run(() => api.patch(`/orders/${order.id}/payment`, {
      payment_status: paid ? "paid" : "unpaid", payment_method: paid ? method : "", payment_reference: paid ? ref : "",
    }), paid ? "Marked paid" : "Marked unpaid");

  const print = () => {
    document.body.classList.add("sm-printing");
    window.print();
    setTimeout(() => document.body.classList.remove("sm-printing"), 500);
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-heading">
            {order.order_number} <StatusBadge status={order.status} /> <StatusBadge status={order.payment_status} />
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={order.status} onValueChange={setStatus} disabled={busy}>
            <SelectTrigger className="w-44" data-testid="order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={print} data-testid="print-invoice-btn"><Printer className="mr-2 h-4 w-4" />Print / PDF</Button>
          <a
            href={waLink(whatsappShareText(order, restaurant), order.customer?.phone)}
            target="_blank" rel="noreferrer" data-testid="whatsapp-share-btn"
            className="inline-flex h-9 items-center rounded-md bg-[#25D366] px-3 text-sm font-medium text-white hover:opacity-90"
          >
            <MessageCircle className="mr-2 h-4 w-4" />WhatsApp
          </a>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Payment</p>
          {order.payment_status === "paid" ? (
            <div className="flex items-center justify-between text-sm">
              <span>Paid via <b className="capitalize">{order.payment_method || "—"}</b> {order.payment_reference && `· ${order.payment_reference}`}</span>
              <Button size="sm" variant="ghost" onClick={() => setPaid(false)} data-testid="mark-unpaid-btn">Mark unpaid</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="w-32" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "upi", "card", "other"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input className="w-44" placeholder="Reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="payment-ref-input" />
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => setPaid(true)} data-testid="mark-paid-btn">Mark paid</Button>
            </div>
          )}
        </div>

        <div className="sm-print-area rounded-xl border border-slate-200">
          <Invoice order={order} restaurant={restaurant} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
